import React, { useState, useEffect } from 'react';
import '../App.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Helmet } from "react-helmet-async";
/**
 * Documents Page - Replicates harpwood.house/documents PHP logic in React
 * 
 * PHP Structure:
 * - getBearerToken() -> gets access token
 * - getListOfFolders() -> gets folder list
 * - getListOfFiles() -> gets files for each folder
 * - downloadFile() -> proxy download via /api/pdf (no browser token)
 * - Accordion with h3 + div structure
 */

interface FileItem {
  name: string;
  serverRelativeUrl: string;
}

interface FolderItem {
  name: string;
  serverRelativeUrl: string;
  files: FileItem[];
}

function Documents() {
  const [folders, setFolders] = useState<FolderItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeFolder, setActiveFolder] = useState<string | null>(null);

  const downloadFile = async (serverRelativeUrl: string, fileName: string) => {
    try {
      const params = new URLSearchParams({
        serverRelativeUrl,
        fileName,
      });

      const apiBase =
        process.env.NODE_ENV === 'production'
          ? ''
          : (process.env.REACT_APP_API_URL || 'http://localhost:3001');

      const response = await fetch(`${apiBase}/api/pdf?${params.toString()}`);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
          errorData.error ||
          `Download failed: ${response.status}`
        );
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download file. Please try again.');
    }
  };

  // Fetch folders and files from API
  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        // In production (Azure Static Web Apps), use relative URL /api/folders
        // In development, use localhost:3001
        const apiUrl = process.env.NODE_ENV === 'production' 
          ? '/api/folders' 
          : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api/folders');
        
        console.log('Fetching from:', apiUrl);
        const response = await fetch(apiUrl);
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const errorMessage = errorData.message || errorData.error || `Server error: ${response.status}`;
          throw new Error(errorMessage);
        }

        const data = await response.json();
        
        // Check if the response contains an error
        if (data.error) {
          throw new Error(data.message || data.error);
        }
        
        setFolders(data.folders || []);

      } catch (err: any) {
        console.error('Fetch error:', err);
        let errorMessage = err.message || 'Failed to load documents';
        
        // Provide more helpful error messages
        if (
          err.message &&
          (err.message.includes('Failed to fetch') ||
           err.message.includes('NetworkError'))
        ) {
          errorMessage =
            'Cannot connect to the server. Please ensure the backend server is running: npm run server';
        } else if (err.message && err.message.includes('tenant identifier')) {
          errorMessage =
            'SharePoint credentials are not configured. Please update sharepoint.env with your Azure AD credentials.';
        }        
        
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, []);

  /**
   * Accordion toggle - Matching PHP jQuery:
   * 
   * $(".accordion h3").click(function(){
   *   $(this).next("div").slideToggle("fast").siblings("div:visible").slideUp("fast");
   *   $(this).toggleClass("active");
   *   $(this).siblings("h3").removeClass("active");
   * });
   */
  const toggleFolder = (folderName: string) => {
    setActiveFolder(activeFolder === folderName ? null : folderName);
  };

  return (
    <>
    <Helmet>
        <title> Important Policies at Harpwood House </title>
        <meta
          name="description"
          content="Explore the policies that guide our care at Harpwood House."
        />
        <link rel="canonical" href="https://www.harpwood.house/documents"/>
      </Helmet>

    <div className="page">
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <Header />

      <main id="main-content">
        <section className="content-section documents-page">
          <div className="documents-container">
            <h1>Documents</h1>

            {loading ? (
              <div className="loading-state">
                <p>Loading documents...</p>
              </div>
            ) : error ? (
              <div className="error-state">
                <h2>Unable to Load Documents</h2>
                <p>{error}</p>
                <div className="sharepoint-fallback-info">
                  <p><strong>Please ensure:</strong></p>
                  <ul>
                    <li>The backend server is running: <code>npm run server</code></li>
                    <li>SharePoint credentials are configured in <code>sharepoint.env</code></li>
                  </ul>
                </div>
              </div>
            ) : (
              /* 
               * Accordion structure matching PHP:
               * <DIV class=accordion>
               *   <h3>$directory->Name</h3>
               *   <DIV>
               *     <ul class="word">
               *       <li><a onclick='downloadURL(...)'>Download $files->Name</a></li>
               *     </ul>
               *   </DIV>
               * </DIV>
               */
              <div className="accordion">
                {folders.map((folder) => (
                  <React.Fragment key={folder.name}>
                    <h3
                      className={activeFolder === folder.name ? 'active' : ''}
                      onClick={() => toggleFolder(folder.name)}
                    >
                      {folder.name}
                    </h3>
                    <div style={{ display: activeFolder === folder.name ? 'block' : 'none' }}>
                      <ul className="word">
                        {folder.files.length > 0 ? (
                          folder.files.map((file) => (
                            <li key={file.name}>
                              <button
                                type="button"
                                onClick={() => downloadFile(folder.serverRelativeUrl, file.name)}
                                title="Click to download"
                                className="download-link-btn"
                              >
                                Download {file.name}
                              </button>
                            </li>
                          ))
                        ) : (
                          <li>No files in this folder</li>
                        )}
                      </ul>
                    </div>
                  </React.Fragment>
                ))}
                
                {/* Staff Portal link matching PHP */}
                <a href="https://staffportal.care"><h3>Staff Portal</h3></a>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
    </>
  );
}

export default Documents;
